import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

import EmailTemplateBuilderServiceQuery from '../../../services/query/emailTemplateBuilder/emailTemplateBuilderService.services';
import Swal from 'sweetalert2';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUITextFieldWithButton } from '../../../atomic/molecules';
import { Grid, Paper, TextField, Box } from '@mui/material';
import SettingSvg from '../../../assets/icons/svg/flaticons/fi-rr-settings-blue.svg';
import Bracket from '../../../assets/icons/svg/flaticons/bracket.svg';
import TimeHelper from '../../../utils/time';
import './SendTestMail.css';
import Helper from '../../../utils/helper';
import { ETBTM } from '../emailTemplateBuilder.classes';

interface Params {
    templateKey: string;
}

export const SendTestMail: FC = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<any>();
    const [emailText, setEmailText] = useState<any>();
    const [previewString, setPreviewString] = useState<any>();
    const [mailContent, setMailContent] = useState(previewString);
    const [otherParameterList, setOtherParameterList] = useState<any>();
    const [fieldValues, setFieldValues] = useState<any>();
    const [requiredParameterList, setRequiredParameterList] = useState<any>();

    const params = useParams<Params>();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const languageCode = searchParams.get('languageCode') || 'tr-TR';
    const countryCode = searchParams.get('countryCode') || 'TR';

    useEffect(() => {
        if (!params.templateKey) {
            return;
        }
        GetSectionHtmlDatasByTemplateId(params.templateKey);
    }, []);

    const GetSectionHtmlDatasByTemplateId = (templateKey: any) => {
        let request = {
            key: templateKey,
            take: 1,
            skip: 1,
            languageCode: languageCode,
            countryCode: countryCode,
        };
        EmailTemplateBuilderServiceQuery.GetTemplatesData(request).then((response: any) => {
            if (response.data.length > 0) {
                setData(response.data[0]);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('emailTemplateBuilder.templateNotFound'),
                    text: response.message,
                });
            }
        });
    };

    useEffect(() => {
        const fetchTemplatePreview = async () => {
            let request = {
                keyword: params.templateKey,
                languageCode: languageCode,
                countryCode: countryCode,
            };

            try {
                const response = await EmailTemplateBuilderServiceQuery.GetTemplatePreview(request);
                if (response?.data?.previewString) {
                    setPreviewString(response.data.previewString);
                    setMailContent(response.data.previewString);
                    setRequiredParameterList(response.parameterList.requiredParameters);
                    setOtherParameterList(response.parameterList.otherParameters);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: t('emailTemplateBuilder.templateNotFound'),
                        text: response.message,
                    });
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: t('emailTemplateBuilder.templateNotFound'),
                    text: error.message,
                });
            }
        };

        fetchTemplatePreview();
    }, [data]);

    useEffect(() => {
        let temp = previewString;
        for (const parameter in fieldValues) {
            const newValue = fieldValues[parameter] || '';

            if (requiredParameterList.includes(parameter)) {
                temp = temp.replace(`[*${parameter}]`, newValue);
            } else {
                temp = temp.replace(`[${parameter}]`, newValue);
            }
        }
        setMailContent(temp);
    }, [fieldValues, mailContent]);

    const SendTestMail = () => {
        EmailTemplateBuilderServiceQuery.SendTestMail({
            body: mailContent,
            subject: data.subject,
            to: emailText,
            languageCode: languageCode,
            countryCode: countryCode,
        }).then((response: any) => {
            if (response) {
                Swal.fire({
                    icon: 'success',
                    title: t('emailTemplateBuilder.mailSend'),
                    text: response.message,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('emailTemplateBuilder.mailSendError'),
                    text: response.message,
                });
            }
        });
    };

    const sendEmail = () => {
        if (emailText == undefined || !Helper.validateEmail(emailText)) {
            Swal.fire({
                icon: 'error',
                title: t('emailTemplateBuilder.pleaseEnterCorrectFormat'),
            });
            return;
        }
        let errorList: any = [];
        if (requiredParameterList.length > 0) {
            requiredParameterList.forEach((requiredParameter: any) => {
                if (fieldValues == undefined || !fieldValues[requiredParameter]) {
                    errorList.push(requiredParameter);
                }
            });
            if (errorList.length > 0) {
                Swal.fire({
                    icon: 'error',
                    title: t('emailTemplateBuilder.pleaseEnterRequiredFields'),
                    text: errorList.join(', '),
                });
                return;
            }
        }
        Swal.fire({
            icon: 'question',
            title: t('general.sureText'),
            text: `Mail ${emailText} adresine gönderilecektir.`,
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                SendTestMail();
            }
        });
    };

    const cleanHTML = DOMPurify.sanitize(mailContent); // your mailContent string with HTML
    const parsedContent = parse(cleanHTML);

    return (
        <SCUIContent>
            {data && (
                <Box className={ETBTM.page}>
                    <Grid className={ETBTM.rootGrid}>
                    <SCUIBreadCrumb
                        dontShowHome={true}
                        homeIconLink={'/home'}
                        onlyTitle={t('emailTemplateBuilder.sendMailTemplate')}
                        components={[
                            {
                                title: t('emailTemplateBuilder.templateBuilder'),
                                link: '/emailTemplateBuilder',
                            },
                            {
                                title: t('emailTemplateBuilder.templateGallery'),
                                link: `/emailTemplateBuilder/templateGallery`,
                            },
                            {
                                title: t('emailTemplateBuilder.sendMailTemplate'),
                                link: `/emailTemplateBuilder/send-test-mail/${params.templateKey}?languageCode=${languageCode}&countryCode=${countryCode}`,
                            },
                        ]}
                    />

                    <Paper className={ETBTM.summaryPaper}>
                        <Grid
                            className={ETBTM.summaryPaperGrid}
                            container
                            size={{
                                xs: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Grid
                                className={ETBTM.summaryHeaderRow}
                                style={{ display: 'flex' }}
                                size={{
                                    xs: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <Grid
                                    className={ETBTM.summaryMetaColumn}
                                    style={{
                                        padding: 10,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                    size={{
                                        xs: 6,
                                        md: 6,
                                        lg: 6,
                                    }}
                                >
                                    <Grid
                                        className={ETBTM.subjectTitle}
                                        size={{
                                            xs: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <h2>Subject: {data.subject}</h2>
                                    </Grid>
                                    <Grid
                                        className={ETBTM.keySubtitle}
                                        sx={{ mt: 2 }}
                                        size={{
                                            xs: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <h4>Key: {data.key}</h4>
                                    </Grid>
                                </Grid>
                                <Grid
                                    className={ETBTM.sendColumn}
                                    size={{
                                        xs: 6,
                                        md: 6,
                                        lg: 6,
                                    }}
                                >
                                    <Grid
                                        className={ETBTM.emailLabelRow}
                                        style={{ display: 'flex', padding: '2rem' }}
                                        size={{
                                            xs: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <p>{t('emailTemplateBuilder.sendEmailAdress')}</p>
                                    </Grid>
                                    <Grid
                                        style={{ display: 'flex', padding: '2rem' }}
                                        size={{
                                            xs: 12,
                                            md: 12,
                                            lg: 12,
                                        }}
                                    >
                                        <Box className={ETBTM.emailCompositeWrap}>
                                            <SCUITextFieldWithButton
                                            buttonText={t('general.send')}
                                            style={{ ml: '2rem' }}
                                            placeholder={'Email'}
                                            onChange={(event: any) => {
                                                setEmailText(event.target.value);
                                            }}
                                            onClick={() => sendEmail()}
                                            onKeyPress={(e: any) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    sendEmail();
                                                }
                                            }}
                                        />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid
                                className={ETBTM.spacerRow}
                                style={{ display: 'flex' }}
                                size={{
                                    xs: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <Grid
                                    style={{ padding: 10 }}
                                    size={{
                                        xs: 6,
                                        md: 6,
                                        lg: 6,
                                    }}
                                ></Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Grid
                        className={ETBTM.mainSplitRow}
                        style={{ display: 'flex' }}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                        }}
                    >
                        <Grid
                            className={ETBTM.mailPropsColumn}
                            size={{
                                xs: 6,
                                md: 6,
                                lg: 6,
                            }}
                        >
                            <Paper className={ETBTM.mailPropsPaper} style={{ height: 'auto', width: '70vh', padding: '3rem' }}>
                                <Grid
                                    className={ETBTM.mailPropsSettingsHeader}
                                    style={{ display: 'flex' }}
                                    size={{
                                        xs: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div>
                                        <img width="100px" height="50px" src={SettingSvg} alt="settings" />
                                    </div>
                                    <h3 style={{ padding: '1rem' }}>Mail Properties</h3>
                                </Grid>
                                <Grid
                                    className={ETBTM.fieldCreatedBy}
                                    style={{ marginTop: '3rem' }}
                                    size={{
                                        xs: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    {data?.createdBy && (
                                        <TextField
                                            className={ETBTM.inputTextfieldCreatedBy}
                                            label={t('emailTemplateBuilder.createBy')}
                                            variant="outlined"
                                            style={{ width: '450px' }}
                                            disabled
                                            value={data.createdBy}
                                        />
                                    )}{' '}
                                </Grid>
                                <Grid
                                    className={ETBTM.fieldCreatedOn}
                                    style={{ marginTop: '3rem', marginBottom: '2rem' }}
                                    size={{
                                        xs: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    {data?.createOn && (
                                        <TextField
                                            className={ETBTM.inputTextfieldCreatedOn}
                                            label={t('emailTemplateBuilder.createOn')}
                                            variant="outlined"
                                            style={{ width: '450px' }}
                                            disabled
                                            value={TimeHelper.formatDateHour(data.createOn)}
                                        />
                                    )}
                                </Grid>
                                <hr />
                                <Grid
                                    className={ETBTM.mailPropsParametersHeader}
                                    style={{ display: 'flex', marginTop: '2rem' }}
                                    size={{
                                        xs: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div>
                                        <img width="100px" height="40px" src={Bracket} alt="bracket" />
                                    </div>
                                    <h3 style={{ padding: '1rem' }}>Parameters</h3>
                                </Grid>
                                <Grid
                                    className={ETBTM.parametersIntro}
                                    style={{ marginLeft: '3rem' }}
                                    size={{
                                        xs: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <h5>{t('emailTemplateBuilder.fillTemplate')}</h5>
                                </Grid>
                                <Grid
                                    className={ETBTM.requiredParametersGrid}
                                    container
                                    spacing={3}
                                    style={{ marginTop: '2rem', boxShadow: ' ' }}
                                >
                                    {requiredParameterList &&
                                        requiredParameterList.map((parameter: any, index: any) => (
                                            <Grid
                                                className={ETBTM.parameterFieldRequired}
                                                key={index}
                                                size={{
                                                    xs: 6,
                                                    sm: 6,
                                                    md: 6,
                                                    lg: 6,
                                                }}
                                            >
                                                <TextField
                                                    className={ETBTM.parameterInputRequired}
                                                    label={parameter}
                                                    variant="outlined"
                                                    required
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        setFieldValues((prevFieldValues: any) => ({
                                                            ...prevFieldValues,
                                                            [parameter]: newValue,
                                                        }));
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                </Grid>
                                <Grid
                                    className={ETBTM.otherParametersGrid}
                                    container
                                    spacing={3}
                                    style={{ marginTop: '2rem' }}
                                >
                                    {otherParameterList &&
                                        otherParameterList.map((parameter: any, index: any) => (
                                            <Grid
                                                className={ETBTM.parameterFieldOptional}
                                                key={index}
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: 6,
                                                    lg: 6,
                                                }}
                                            >
                                                <TextField
                                                    className={ETBTM.parameterInputOptional}
                                                    label={parameter}
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        setFieldValues((prevFieldValues: any) => ({
                                                            ...prevFieldValues,
                                                            [parameter]: newValue,
                                                        }));
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                </Grid>
                            </Paper>
                        </Grid>
                        <Grid
                            className={ETBTM.previewColumn}
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginLeft: '2rem',
                                padding: '3rem',
                                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
                            }}
                            size={{
                                xs: 6,
                                md: 6,
                                lg: 6,
                            }}
                        >
                            <div className={ETBTM.previewContent}>{parsedContent}</div>
                        </Grid>
                    </Grid>
                </Grid>
                </Box>
            )}
        </SCUIContent>
    );
};
